import type { PairAccountingPair } from '../services/pairTradingApi';

/**
 * Per-pair PnL and return, marked to the live price — the two numbers the PnL
 * chart plots.
 *
 * `return` mirrors the engine's own `calculate_pair_return`
 * (algorithm/pair/worker/mixins/pnl.py): unrealized PnL over the pair's
 * ACQUISITION value, where the two legs enter with different leverage —
 *
 *   stock leg   → full cash outlay        (qty × cost, raw VND)
 *   futures leg → notional × margin FLOOR (qty × cost × multiplier × rate)
 *
 * The floor is a normalising yardstick, not the collateral posted: the engine
 * uses it so that funding or withdrawing idle cash cannot move an open pair's
 * TP/CL. Passing the live `minMarginRate` from
 * GET /pair/allocation/:dc/balance keeps this in step with the broker registry
 * (VN30F = 22.7% today, VSDC floor 17%); hardcoding it would silently diverge.
 *
 * Unit rule ([[feedback-vnd-units]]) — the accounting endpoint's legs are NOT in
 * one unit, same as in `pairPositionPnl.ts`:
 *   stock   → costPrice is raw VND/share, live price is thousand VND ⇒ ×1000
 *   futures → costPrice is index points, live price is points     ⇒ ×multiplier
 * Every value returned here is raw VND except `returnPct`.
 */

const STOCK_UNIT = 1000;

export interface PairMetrics {
    pairId: string;
    /** Raw VND, live-marked. Falls back to the backend mark for a leg with no tick. */
    pnl: number;
    /** The same PnL split by leg — these two always sum back to `pnl`. */
    derivPnl: number;
    equityPnl: number;
    /** Raw VND. Stock cost + futures notional × margin floor. */
    acquisition: number;
    /** pnl / acquisition × 100, or null when acquisition is 0 (nothing filled). */
    returnPct: number | null;
    /** False when any leg had to fall back to the backend's mark. */
    live: boolean;
    /** False when a leg has neither a tick nor the backend's mark: pnl and return are unknown. */
    priced?: boolean;
    /** Instruments in the pair, for the series label. */
    legs: string[];
}

const isFutures = (instrument: string): boolean => instrument.startsWith('HNXDS:');

const tickerOf = (instrument: string): string => {
    const i = instrument.indexOf(':');
    return i >= 0 ? instrument.slice(i + 1) : instrument;
};

export function computePairMetrics(
    pairs: PairAccountingPair[],
    priceOf: (ticker: string) => number | null,
    minMarginRate: number,
    futuresMultiplier: number,
): PairMetrics[] {
    const out: PairMetrics[] = [];

    for (const pair of pairs) {
        let derivPnl = 0;
        let equityPnl = 0;
        let acquisition = 0;
        let live = true;
        // a leg with neither a tick nor the backend's mark: the pair's PnL is unknown
        let unpriced = false;
        const legs: string[] = [];

        for (const leg of pair.legs ?? []) {
            if (!leg?.instrument || !leg.quantity) continue;
            legs.push(tickerOf(leg.instrument));

            const future = isFutures(leg.instrument);
            const unit = future ? futuresMultiplier : STOCK_UNIT;
            const sign = leg.side === 'SHORT' ? -1 : 1;
            const costVnd = leg.costPrice * (future ? futuresMultiplier : 1);

            const tick = priceOf(tickerOf(leg.instrument));
            let legPnl: number;
            if (tick != null && tick > 0) {
                legPnl = sign * leg.quantity * (tick * unit - costVnd);
            } else {
                // No tick for this instrument yet — keep the backend's own mark
                // rather than dropping the pair to zero.
                legPnl = leg.pnl ?? 0;
                live = false;
                if (leg.pnl == null) unpriced = true;
            }
            if (future) derivPnl += legPnl;
            else equityPnl += legPnl;

            // Futures collateral enters at the floor rate; stock at full cash.
            acquisition += future
                ? leg.quantity * costVnd * minMarginRate
                : leg.quantity * costVnd;
        }

        if (!legs.length) continue;
        // What the roll banked on the futures leg it closed: the engine's TP/CL counts it.
        derivPnl += pair.realizedPnl ?? 0;
        const pnl = derivPnl + equityPnl;
        out.push({
            pairId: pair.pairId,
            pnl,
            derivPnl,
            equityPnl,
            acquisition,
            returnPct: !unpriced && acquisition > 0 ? (pnl / acquisition) * 100 : null,
            live,
            priced: !unpriced,
            legs,
        });
    }

    return out;
}

export interface LegPnlTotals {
    /** Futures leg across every open pair, raw VND. */
    deriv: number;
    /** Stock basket across every open pair, raw VND. */
    equity: number;
    /** deriv + equity. Plotted as its own line, so it must be their exact sum. */
    total: number;
}

/**
 * The three lines the PnL chart draws, summed over open pairs.
 *
 * Deliberately in VND, not percent: the point of the third line is that it is
 * the sum of the other two, and two returns measured against two different
 * denominators (stock at full cash, futures at the margin floor) do not add.
 */
export function aggregateLegPnl(metrics: PairMetrics[]): LegPnlTotals {
    const deriv = metrics.reduce((s, m) => s + m.derivPnl, 0);
    const equity = metrics.reduce((s, m) => s + m.equityPnl, 0);
    return { deriv, equity, total: deriv + equity };
}

/**
 * Return of the whole book: Σ PnL over Σ acquisition, in percent.
 *
 * A capital-weighted average of the per-pair returns, NOT an exit signal — the
 * engine still evaluates TP/CL per pair, so this figure can cross the threshold
 * with no pair exiting, and a pair can exit while it sits well inside. Null when
 * nothing is open, so a caller shows nothing rather than 0%.
 */
export function aggregateReturnPct(metrics: PairMetrics[]): number | null {
    if (!metrics.length) return null;
    // a pair with no price has no return; counting it as 0 would pull the average toward it
    const priced = metrics.filter((m) => m.priced !== false);
    const capital = priced.reduce((s, m) => s + m.acquisition, 0);
    if (capital <= 0) return null;
    return (priced.reduce((s, m) => s + m.pnl, 0) / capital) * 100;
}

/**
 * TP/SL expressed in VND for the whole book: Σ threshold × each pair's own
 * acquisition value.
 *
 * This is a SCALE, not an exit rule — the engine evaluates TP/CL per pair, so
 * the book can sit inside this band while one pair exits, or cross it with none
 * exiting. The chart labels it as such; without it the VND axis gives the reader
 * no sense of how far the money is from where the bot acts.
 */
export function aggregateThresholds(
    metrics: PairMetrics[],
    tpPct: number,
    slPct: number,
): { tp: number; sl: number } | null {
    if (!metrics.length) return null;
    const capital = metrics.reduce((s, m) => s + m.acquisition, 0);
    if (capital <= 0) return null;
    const { tp, sl } = normaliseThresholds(tpPct, slPct);
    return { tp: (tp / 100) * capital, sl: (sl / 100) * capital };
}

/**
 * TP above zero, SL below it — the shape the chart and the backend both need.
 *
 * The pair form holds BOTH as positive magnitudes (the SL input is `min={0}`,
 * read as "close at a 2% loss"); the signed form only exists on the wire, where
 * PairSettingsService.normaliseIncoming turns 2 into -0.02 for the bot. A caller
 * that forwards the raw form value to a signed-percent axis draws SL on top of
 * TP, which is why this lives in one place instead of at each call site.
 */
export function normaliseThresholds(tp: number, sl: number): { tp: number; sl: number } {
    return { tp: Math.abs(tp), sl: -Math.abs(sl) };
}

/* ────────────────────────── leg notional (the two price lines) ───────────── */

export interface LegNotionalInput {
    /** Live futures price, index points. Null ⇒ the futures line has no point. */
    futPrice: number | null;
    /** VND per point per contract, from the broker profile. */
    multiplier: number;
    /** Contracts the line represents: held size when filled, 1 when flat. */
    contracts: number;
    /**
     * The basket: held quantities, or the spread basket's unrounded shares
     * (see `spreadBasket`). Prices are in the leg's display unit.
     */
    stocks: Array<{ quantity: number; price: number | null; unit: number }>;
}

export interface LegNotional {
    /** futPrice × multiplier × contracts, raw VND. Null when no quote. */
    futValue: number | null;
    /** Σ quantity × price × unit, raw VND. Null when any leg lacks a quote. */
    equityValue: number | null;
}

/**
 * The two lines the price pane draws, both in raw VND.
 *
 * This is the pair's own arithmetic, which is what makes the two comparable at
 * all: the engine sizes the basket FROM the futures notional
 * (`stock_leg_value = future_ref × multiplier`, then split by ratio and rounded
 * to board lots), so expressing both legs as notional puts a ~195m futures line
 * next to a ~192m basket line on ONE axis. Plotting a 1,970-point price against
 * a 60-thousand-VND price would need two y-scales, and a two-scale chart lets
 * the gap be positioned anywhere the scales are chosen — the gap would be
 * fiction.
 *
 * And the gap is the whole point: for a short-futures / long-stock pair,
 *   PnL(t) = (equityValue(t) − futValue(t)) − (equityValue(0) − futValue(0))
 * so the vertical distance between the lines, measured against its value at
 * entry, IS the money in the pane below.
 */
export function computeLegNotional(input: LegNotionalInput): LegNotional {
    const futValue =
        input.futPrice != null && input.futPrice > 0
            ? input.futPrice * input.multiplier * input.contracts
            : null;

    let equityValue: number | null = 0;
    for (const leg of input.stocks) {
        if (!leg.quantity) continue; // a slice below one board lot contributes nothing
        if (leg.price == null || !(leg.price > 0)) {
            equityValue = null;
            break;
        }
        equityValue += leg.quantity * leg.price * leg.unit;
    }
    return { futValue, equityValue };
}
